import CheckIcon from '@mui/icons-material/Check';
import { Select, type SxProps } from '@mui/material';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { MenuItem } from '../menuItem/menuItem';

type Option = {
  label: string;
  type: string;
  avatar?: string;
  dataTestId?: string;
};

type SelectorOptionProps = {
  option: Option;
  selectedOptionType?: string;
};

type SelectorProps = {
  options: Option[];
  selectedOptionType: string;
  onChange: (event: any) => void;
  renderValue?: (label: string) => string;
  sx?: SxProps;
  disabled?: boolean;
  optionsPosition?: 'right' | 'left';
  readOnly?: boolean;
  testId?: string;
  menuItemSx?: SxProps;
  showIcon?: boolean;
};

const SelectorOption = ({
  option,
  selectedOptionType,
}: SelectorOptionProps) => (
  <Box
    data-testid={option?.dataTestId}
    sx={{
      display: 'flex',
      alignItems: 'center',
      width: selectedOptionType === option.type ? 'calc(100% - 16px)' : '100%',
    }}
  >
    {option.avatar && (
      <Box
        sx={{
          mr: 1,
          height: 20,
          width: 20,
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <img
          alt={option.label}
          src={option.avatar}
          style={{ objectFit: 'cover', height: '100%' }}
        />
      </Box>
    )}
    <Typography
      variant='bodyMedium'
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...(option.avatar ? { width: 'calc(100% - 28px)' } : {}),
        fontWeight: 500,
      }}
    >
      {option.label}
    </Typography>
  </Box>
);

export const Selector = ({
  options,
  selectedOptionType,
  onChange,
  sx,
  menuItemSx,
  disabled,
  renderValue,
  optionsPosition = 'left',
  readOnly,
  testId = 'selector',
  showIcon = true,
}: SelectorProps) => {
  const theme = useTheme();
  return (
    <Select
      data-testid={testId}
      inputProps={{ readOnly }}
      value={selectedOptionType}
      disabled={disabled}
      readOnly={readOnly}
      onChange={onChange}
      renderValue={(value) => {
        const option = options.find((elem) => value === elem.type);
        if (option) {
          let newOption = { ...option };
          if (renderValue)
            newOption = { ...newOption, label: renderValue(newOption.label) };
          return <SelectorOption option={newOption} />;
        }
        return <></>;
      }}
      sx={{
        height: 32,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.color.surfaceContainerVariant,
        '.MuiOutlinedInput-notchedOutline': { border: 'none' },
        '.MuiSelect-outlined': {
          padding: 0,
          paddingLeft: '12px',
          paddingRight: disabled ? '8px !important' : '28px !important',
          display: 'flex',
          alignItems: 'center',
        },
        '.MuiSvgIcon-root': {
          right: '4px',
          color: theme.color.onSurface,
          display: disabled || readOnly ? 'none' : undefined,
        },
        '.MuiInputBase-input': {
          cursor: readOnly ? 'text' : undefined,
        },
        ...sx,
      }}
      MenuProps={{
        anchorOrigin: { horizontal: optionsPosition, vertical: 33 },
        transformOrigin: { horizontal: optionsPosition, vertical: 0 },
        MenuListProps: {
          sx: {
            padding: 0,
            '&& .Mui-selected': { backgroundColor: theme.color.background },
            '&& .Mui-selected:focus': {
              backgroundColor: theme.color.background,
            },
            '&& .Mui-selected:hover': { backgroundColor: theme.color.disabled },
            '& .MuiMenuItem-root': { backgroundColor: theme.color.background },
            '& .MuiMenuItem-root:hover': {
              backgroundColor: theme.color.disabled,
            },
          },
        },
        PaperProps: {
          sx: { borderRadius: theme.borderRadius.md },
        },
      }}
    >
      {options.map((option) => (
        <MenuItem
          key={option.type}
          value={option.type}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '200px',
            paddingX: '12px',
            paddingY: '5px',
            ...menuItemSx,
          }}
        >
          <SelectorOption
            option={option}
            selectedOptionType={selectedOptionType}
          />
          {showIcon && selectedOptionType === option.type && (
            <Box
              sx={{
                width: '16px',
                height: '16px',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <CheckIcon fontSize='inherit' />
            </Box>
          )}
        </MenuItem>
      ))}
    </Select>
  );
};
